"use client"

import { useState, useEffect } from "react"
import { ShoppingBag, Package, Coins, Loader2 } from "lucide-react"
import { useSession } from "next-auth/react"
import Link from "next/link"
import { ShopPurchaseModal } from "@/components/shop/ShopPurchaseModal"
import { ShopPackCard } from "@/components/shop/ShopPackCard"

interface ShopItem {
    sitem_id: number
    nome: string
    amount: number
    enchant: number
    valor: number
}

interface ShopPack {
    spack_id: number
    nome: string
    imagem: string | null
    valor: number
    server_id: string | null
    items: ShopItem[]
}

interface ShopCategory {
    scat_id: number
    nome: string
    server_id: string | null
    packs: ShopPack[]
}

export default function ShopPage() {
    const { data: session } = useSession()
    const [categories, setCategories] = useState<ShopCategory[]>([])
    const [balance, setBalance] = useState(0)
    const [loading, setLoading] = useState(true)
    const [activeCategory, setActiveCategory] = useState<number | null>(null)
    const [selectedPack, setSelectedPack] = useState<ShopPack | null>(null)
    const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false)
    const [creditsPerUsd, setCreditsPerUsd] = useState(25)

    useEffect(() => {
        const fetchShop = async () => {
            try {
                const res = await fetch("/api/shop")
                if (res.ok) {
                    const data = await res.json()

                    // Flatten server_id into packs for easier modal handling
                    const processedCategories = (data.categories || []).map((cat: any) => ({
                        ...cat,
                        packs: cat.packs.map((p: any) => ({ ...p, server_id: cat.server_id }))
                    }))

                    // Filter categories based on user servers if logged in
                    let filtered = processedCategories
                    if (session && data.userServers) {
                        const userServers = data.userServers || []
                        filtered = filtered.filter((cat: ShopCategory) =>
                            !cat.server_id || userServers.includes(cat.server_id)
                        )
                    }

                    setCategories(filtered)
                    setBalance(data.balance || 0)
                    if (data.economy?.credits_per_usd) {
                        setCreditsPerUsd(Number(data.economy.credits_per_usd))
                    }
                    if (filtered.length > 0) {
                        setActiveCategory(filtered[0].scat_id)
                    }
                }
            } catch {
                console.error("Failed to fetch shop")
            } finally {
                setLoading(false)
            }
        }
        fetchShop()
    }, [session])

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-slate-500" />
            </div>
        )
    }

    const activeCat = categories.find((c) => c.scat_id === activeCategory)

    return (
        <div className="min-h-screen bg-transparent">
            <div className="container mx-auto px-4 py-16">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                        <ShoppingBag className="w-3 h-3" />
                        Item Shop
                    </div>
                    <h1 className="text-5xl font-black tracking-tighter text-white mb-4 uppercase">
                        Dragon <span className="text-purple-500">Store</span>
                    </h1>
                    <p className="text-slate-400 text-lg max-w-md mx-auto italic">
                        Enhance your adventure with exclusive items and packs.
                    </p>
                    {session ? (
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/80 border border-slate-700 rounded-xl">
                                <Coins className="w-4 h-4 text-amber-400" />
                                <span className="text-white font-bold">{balance.toLocaleString()}</span>
                                <span className="text-slate-400 text-sm">Credits</span>
                            </div>
                            <Link
                                href="/dashboard/shop"
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-amber-500/20 transition-all active:scale-95"
                            >
                                Transfer Coins (1:1) &rarr;
                            </Link>
                        </div>
                    ) : (
                        <p className="mt-6 text-slate-500 text-sm">
                            <Link href="/login" className="text-purple-400 hover:text-purple-300 font-bold">
                                Log in
                            </Link>{" "}
                            to purchase items.
                        </p>
                    )}
                </div>

                {categories.length === 0 ? (
                    <div className="text-center py-20">
                        <ShoppingBag className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                        <p className="text-slate-500 text-lg">The shop is being prepared. Check back soon!</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Category sidebar */}
                        <div className="lg:col-span-1">
                            <div className="sticky top-24 space-y-2">
                                <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4 px-3">
                                    Categories
                                </h3>
                                {categories.map((cat) => (
                                    <button
                                        key={cat.scat_id}
                                        onClick={() => {
                                            setActiveCategory(cat.scat_id)
                                            setSelectedPack(null)
                                        }}
                                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeCategory === cat.scat_id
                                            ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                                            : "text-slate-400 hover:text-white hover:bg-slate-800/50 border border-transparent"
                                            }`}
                                    >
                                        <span>{cat.nome}</span>
                                        <span className="text-xs opacity-50">{cat.packs.length}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Packs grid */}
                        <div className="lg:col-span-3">
                            {activeCat && (
                                <>
                                    <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-tight">
                                        {activeCat.nome}
                                    </h2>
                                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                        {activeCat.packs.map((pack) => (
                                            <ShopPackCard
                                                key={pack.spack_id}
                                                pack={pack}
                                                variant="user"
                                                creditsPerUsd={creditsPerUsd}
                                                onBuy={() => {
                                                    setSelectedPack(pack)
                                                    setIsPurchaseModalOpen(true)
                                                }}
                                            />
                                        ))}
                                    </div>

                                    {activeCat.packs.length === 0 && (
                                        <div className="text-center py-16">
                                            <Package className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                                            <p className="text-slate-500">No packs in this category yet.</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ShopPurchaseModal
                pack={selectedPack}
                isOpen={isPurchaseModalOpen}
                onClose={() => setIsPurchaseModalOpen(false)}
                onPurchaseSuccess={(newBalance) => {
                    setBalance(newBalance)
                    // Optional: Refresh shop data
                }}
            />
        </div>
    )
}
